import { useDeferredValue, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ordersApi } from '@/services/orders'
import type { JobOrderInfo, StoreOrderSummary } from '@/types'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'

interface OrderSelectorProps {
  label?: string
  placeholder?: string
  value?: string | null
  selectedOrder?: StoreOrderSummary | JobOrderInfo | null
  onSelect: (order: StoreOrderSummary | null) => void
  disabled?: boolean
  error?: string
}

const getSelectedLabel = (selectedOrder?: StoreOrderSummary | JobOrderInfo | null, value?: string | null) => {
  if (selectedOrder?.reference_id) {
    return selectedOrder.reference_id
  }

  if (value) {
    return value
  }

  return null
}

export function OrderSelector({
  label = 'Booking ID',
  placeholder = 'Search and select a booking',
  value,
  selectedOrder,
  onSelect,
  disabled = false,
  error,
}: OrderSelectorProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const deferredSearch = useDeferredValue(search.trim())

  const query = useQuery({
    queryKey: ['orders', 'picker', deferredSearch],
    queryFn: () => ordersApi.listOrders({ page: 1, page_size: 10, order_id: deferredSearch || undefined }),
    enabled: open,
    staleTime: 30_000,
  })

  const selectedLabel = getSelectedLabel(selectedOrder, value)
  const options = useMemo(() => query.data?.results ?? [], [query.data])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="order-search">{label}</Label>
        {selectedLabel ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground"
            onClick={() => onSelect(null)}
            disabled={disabled}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        ) : null}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'h-auto min-h-10 w-full justify-between px-3 py-2 text-left font-normal',
              !selectedLabel && 'text-muted-foreground',
              error && 'border-destructive text-destructive',
            )}
          >
            <span className="truncate">{selectedLabel ? `Booking #${selectedLabel}` : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-0">
          <div className="border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="order-search"
                placeholder="Search by booking ID"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {query.isLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading bookings...
              </div>
            ) : query.isError ? (
              <p className="px-2 py-6 text-sm text-destructive">{getErrorMessage(query.error)}</p>
            ) : options.length > 0 ? (
              options.map((order) => {
                const isSelected = selectedLabel === order.reference_id

                return (
                  <button
                    key={`${order.id}-${order.reference_id}`}
                    type="button"
                    className="flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left hover:bg-muted"
                    onClick={() => {
                      onSelect(order)
                      setOpen(false)
                    }}
                  >
                    <Check className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">Booking #{order.reference_id}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {[order.customer_name, order.status_label, order.type_label].filter(Boolean).join(' • ') || 'No extra details'}
                      </p>
                      {order.address_label ? (
                        <p className="truncate text-xs text-muted-foreground">{order.address_label}</p>
                      ) : null}
                    </div>
                  </button>
                )
              })
            ) : (
              <p className="px-2 py-6 text-sm text-muted-foreground">No bookings matched this search.</p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {selectedLabel ? (
        <div className="rounded-xl border border-border/70 bg-muted/20 p-3 text-sm">
          <p className="font-medium text-foreground">Linked booking #{selectedLabel}</p>
          {selectedOrder ? (
            <p className="mt-1 text-muted-foreground">
              {[
                'customer_name' in selectedOrder ? selectedOrder.customer_name : null,
                'status_label' in selectedOrder ? selectedOrder.status_label : null,
                'type_label' in selectedOrder ? selectedOrder.type_label : null,
              ].filter(Boolean).join(' • ') || 'Booking linked to this job'}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Leave this empty if the job is not tied to a booking.</p>
      )}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  )
}

export default OrderSelector